/**
 * RecipeModal — Full recipe view overlay.
 * Ported from the personal prototype's RecipeModal.js.
 * Shows hero image, meta chips, ingredients, and numbered instructions.
 */
import React from 'react';

interface Ingredient {
  name: string;
  amount?: number;
  unit?: string;
  original?: string;
  aisle?: string;
}

interface Instruction {
  number?: number;
  step: string;
}

interface Recipe {
  recipeId: string;
  title: string;
  image?: string;
  readyInMinutes?: number;
  servings?: number;
  sourceUrl?: string;
  summary?: string;
  cuisines?: string[];
  dishTypes?: string[];
  diets?: string[];
  ingredients?: Ingredient[];
  instructions?: Instruction[];
  source?: string;
}

interface Props {
  recipe: Recipe;
  onClose: () => void;
  onEdit?: (recipe: Recipe) => void;
  onDelete?: (recipeId: string) => void;
}

const RecipeModal: React.FC<Props> = ({ recipe, onClose, onEdit, onDelete }) => {
  return (
    <div className="recipe-modal-overlay" onClick={onClose}>
      <div className="recipe-modal" onClick={(e) => e.stopPropagation()}>
        {/* Hero Image */}
        <div className="recipe-modal-image">
          {recipe.image ? (
            <img src={recipe.image} alt={recipe.title} />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                background: 'var(--color-bg-alt)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '4rem',
              }}
            >
              🍽️
            </div>
          )}
          {recipe.source && (
            <span
              className="meal-card-source"
              style={{ top: '12px', left: '12px', fontSize: '0.72rem' }}
            >
              {recipe.source === 'claude-photo' && '📷 Photo Scan'}
              {recipe.source === 'claude-url' && '🔗 URL Import'}
              {recipe.source === 'manual' && '✏️ Manual'}
              {recipe.source === 'import' && '📥 Import'}
            </span>
          )}
          <button className="recipe-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="recipe-modal-body">
          <h2 className="recipe-modal-title">{recipe.title}</h2>

          {/* Meta Chips */}
          <div className="recipe-modal-meta">
            {(recipe.readyInMinutes ?? 0) > 0 && (
              <span className="recipe-meta-chip">
                ⏱ {recipe.readyInMinutes} min
              </span>
            )}
            {(recipe.servings ?? 0) > 0 && (
              <span className="recipe-meta-chip">
                👥 {recipe.servings} servings
              </span>
            )}
            {recipe.cuisines?.map((c) => (
              <span key={c} className="recipe-meta-chip">
                🌍 {c}
              </span>
            ))}
            {recipe.dishTypes?.map((d) => (
              <span key={d} className="recipe-meta-chip">
                🍽️ {d}
              </span>
            ))}
          </div>

          {/* Source URL */}
          {recipe.sourceUrl && (
            <a
              href={recipe.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="recipe-source-link"
            >
              🔗 View Original Recipe →
            </a>
          )}

          {/* Summary */}
          {recipe.summary && (
            <div className="recipe-section">
              <h3 className="recipe-section-heading">📝 Summary</h3>
              <p className="recipe-summary">{recipe.summary}</p>
            </div>
          )}

          {/* Ingredients */}
          {recipe.ingredients && recipe.ingredients.length > 0 && (
            <div className="recipe-section">
              <h3 className="recipe-section-heading">
                🥘 Ingredients
                <span className="recipe-section-count">
                  {recipe.ingredients.length}
                </span>
              </h3>
              <ul className="recipe-ingredients">
                {recipe.ingredients.map((ing, idx) => (
                  <li key={idx} className="recipe-ingredient">
                    <span className="recipe-ingredient-bullet">●</span>
                    <span>{ing.original || ing.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {recipe.instructions && recipe.instructions.length > 0 && (
            <div className="recipe-section">
              <h3 className="recipe-section-heading">
                👨‍🍳 Instructions
                <span className="recipe-section-count">
                  {recipe.instructions.length} step
                  {recipe.instructions.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <ol className="recipe-steps">
                {recipe.instructions.map((inst, idx) => (
                  <li key={idx} className="recipe-step">
                    <span className="recipe-step-number">{idx + 1}</span>
                    <span className="recipe-step-text">{inst.step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Action Buttons */}
          {(onEdit || onDelete) && (
            <div
              style={{
                display: 'flex',
                gap: '8px',
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: '1px solid var(--color-bg-alt)',
              }}
            >
              {onEdit && (
                <button
                  className="btn btn-secondary"
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => onEdit(recipe)}
                >
                  ✏️ Edit Recipe
                </button>
              )}
              {onDelete && (
                <button
                  className="btn btn-secondary"
                  style={{
                    padding: '10px 18px',
                    color: 'var(--color-error)',
                  }}
                  onClick={() => onDelete(recipe.recipeId)}
                >
                  🗑️ Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeModal;
